import { Modal, Button, Col } from 'antd';
import { FieldRow, Form, spinner } from '@/components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  GeneratePersonalizationSummaryQRCodeArgs,
  useUserContextSelector,
} from '@/redux';
import { reportsHttp } from '@/core';
import { useCallback, useEffect } from 'react';
import { browserFileDownload } from '@/utils';
import { ReportingClientSelect } from './ReportingClientSelect';
import { ReportingCommunitySelect } from './ReportingCommunitySelect';
import { useFormikContext } from 'formik';
import { useReportingProjectsLookupSource } from '../Projects.Common';
import { useReportingHomesLookupSource } from '../Homes.Common';
import { isArray } from 'lodash';

const schema = Yup.object().shape({
  clientId: Yup.string().nullable(),
  communityId: Yup.string().nullable().required(),
  projectId: Yup.string().nullable().required(),
  homeIds: Yup.array()
    .of(Yup.string().nullable().required('Home ID is required'))
    .min(1, 'Home must not be empty')
    .required(),
});

export type GeneratePersonalizationSummaryQRCodeFormValue = OmitIndex<
  Yup.InferType<typeof schema>
>;

const INITIAL_VALUE: GeneratePersonalizationSummaryQRCodeFormValue = {
  clientId: null!,
  communityId: null!,
  projectId: null!,
  homeIds: [],
};

interface Props {
  onClose: () => any;
}

const useSubmit = (props: Props) => {
  const { onClose } = props;
  return useCallback(
    (args: GeneratePersonalizationSummaryQRCodeArgs) => {
      spinner.show();
      reportsHttp
        .generatePersonalizationSummaryQRCode(args)
        .then((file) => {
          browserFileDownload(file);
        })
        .finally(() => {
          onClose();
          spinner.hide();
        });
    },
    [onClose],
  );
};

export function GeneratePersonalizationSummaryQRCodeModal(props: Props) {
  const { onClose: onCancel } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'reports.personalizationSummaryQRCode',
  });

  const clientId = useUserContextSelector((x) => x.clientId);
  const handleSubmit = useSubmit(props);

  return (
    <Modal
      title={t('title')}
      visible
      onCancel={onCancel}
      footer={[
        <Button key="back" type="default" onClick={onCancel}>
          {t('cancel')}
        </Button>,
        <Form.Submit
          uid="reports-personalization-summary-qr-code"
          key="submit"
          type="primary"
        >
          {t('runReport')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<GeneratePersonalizationSummaryQRCodeFormValue>
        uid="reports-personalization-summary-qr-code"
        i18n="reports.personalizationSummaryQRCode"
        initialValues={{
          ...INITIAL_VALUE,
          clientId:
            clientId && isArray(clientId) && clientId.length === 1
              ? clientId[0]
              : INITIAL_VALUE.clientId,
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        <FormContent />
      </Form.Formik>
    </Modal>
  );
}

function FormContent() {
  const { values, setFieldValue } =
    useFormikContext<GeneratePersonalizationSummaryQRCodeFormValue>();
  const { clientId, communityId, projectId } = values;
  const projectsDataSource = useReportingProjectsLookupSource(
    true,
    communityId ?? undefined,
    clientId ?? undefined,
  );
  const homesDataSource = useReportingHomesLookupSource(
    clientId ?? undefined,
    communityId ?? undefined,
    projectId ?? undefined,
    true,
  );

  const [isClientUser, isBfpAdmin] = useUserContextSelector((x) => [
    x.hasRole('CLIENT_USER'),
    x.hasRole('BFP_ADMIN'),
  ]);
  useEffect(() => {
    setFieldValue('homeIds', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setFieldValue('projectId', undefined);
    setFieldValue('homeIds', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  useEffect(() => {
    setFieldValue('communityId', undefined);
    setFieldValue('projectId', undefined);
    setFieldValue('homeIds', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <>
      {(isBfpAdmin || isClientUser) && (
        <FieldRow>
          <Col span={24}>
            <ReportingClientSelect name="clientId" />
          </Col>
        </FieldRow>
      )}
      <FieldRow>
        <Col span={24}>
          <ReportingCommunitySelect
            name="communityId"
            clientId={clientId ?? undefined}
            required
          />
        </Col>
      </FieldRow>
      <FieldRow>
        <Col span={24}>
          <Form.AsyncSelect
            name="projectId"
            key={`${communityId}${clientId}`}
            dataSource={projectsDataSource}
            required
            disabled={!communityId}
          />
        </Col>
      </FieldRow>
      <FieldRow>
        <Col span={24}>
          <Form.AsyncSelect
            key={`${communityId}${clientId}${projectId}`}
            name="homeIds"
            dataSource={homesDataSource}
            mode="multiple"
            required
            disabled={!projectId}
          />
        </Col>
      </FieldRow>
    </>
  );
}
